import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  Divider,
  Paper,
  Grid,
  Avatar,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Close as CloseIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  School as SchoolIcon,
  Schedule as ScheduleIcon,
  Visibility as VisibilityIcon,
  Reply as ReplyIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { scoutMessageService } from '../../services/scoutMessageService';
import {
  ScoutMessageDetailResponse,
  ScoutMessageStatus,
  MessageReplyItem,
} from '../../types/scout-message';
import { LinkifiedText } from '../Common/LinkifiedText';

interface ScoutMessageDetailModalProps {
  open: boolean;
  messageId: number | null;
  companyId?: number;
  onClose: () => void;
}

export const ScoutMessageDetailModal: React.FC<
  ScoutMessageDetailModalProps
> = ({ open, messageId, companyId, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [messageDetail, setMessageDetail] = useState<
    ScoutMessageDetailResponse['data'] | null
  >(null);

  useEffect(() => {
    if (open && messageId) {
      fetchMessageDetail();
    }
  }, [open, messageId]);

  const fetchMessageDetail = async () => {
    if (!messageId || !companyId) return;

    try {
      setLoading(true);
      setError(null);
      const response = await scoutMessageService.getMessageDetail(
        companyId,
        messageId
      );
      setMessageDetail(response.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'エラーが発生しました');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: ScoutMessageStatus) => {
    switch (status) {
      case ScoutMessageStatus.SENT:
        return 'info';
      case ScoutMessageStatus.DELIVERED:
        return 'primary';
      case ScoutMessageStatus.READ:
        return 'warning';
      case ScoutMessageStatus.REPLIED:
        return 'success';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: ScoutMessageStatus) => {
    switch (status) {
      case ScoutMessageStatus.SENT:
        return '送信済み';
      case ScoutMessageStatus.DELIVERED:
        return '配信済み';
      case ScoutMessageStatus.READ:
        return '開封済み';
      case ScoutMessageStatus.REPLIED:
        return '返信あり';
      default:
        return status;
    }
  };

  const getStatusIcon = (status: ScoutMessageStatus) => {
    switch (status) {
      case ScoutMessageStatus.SENT:
        return <SendIcon fontSize="small" />;
      case ScoutMessageStatus.DELIVERED:
        return <EmailIcon fontSize="small" />;
      case ScoutMessageStatus.READ:
        return <VisibilityIcon fontSize="small" />;
      case ScoutMessageStatus.REPLIED:
        return <ReplyIcon fontSize="small" />;
      default:
        return null;
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleClose = () => {
    setMessageDetail(null);
    setError(null);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '60vh', maxHeight: '90vh' },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="h6">スカウトメッセージ詳細</Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {messageDetail && !loading && (
          <Box sx={{ p: 1 }}>
            {/* メッセージ基本情報 */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      mb: 2,
                    }}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      {messageDetail.message.subject}
                    </Typography>
                    <Chip
                      icon={
                        getStatusIcon(messageDetail.message.status) || undefined
                      }
                      label={getStatusLabel(messageDetail.message.status)}
                      color={getStatusColor(messageDetail.message.status)}
                      size="small"
                    />
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                      <PersonIcon />
                    </Avatar>
                    <Box>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 'medium' }}
                      >
                        {messageDetail.message.recipient.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        受信者
                      </Typography>
                    </Box>
                  </Box>

                  {messageDetail.message.recipient.university && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <SchoolIcon
                        sx={{ mr: 1, color: 'text.secondary' }}
                        fontSize="small"
                      />
                      <Typography variant="body2" color="text.secondary">
                        {messageDetail.message.recipient.university}
                        {messageDetail.message.recipient.faculty &&
                          ` ${messageDetail.message.recipient.faculty}`}
                      </Typography>
                    </Box>
                  )}

                  {messageDetail.message.recipient.graduation_year && (
                    <Typography variant="body2" color="text.secondary">
                      卒業予定年:{' '}
                      {messageDetail.message.recipient.graduation_year}年
                    </Typography>
                  )}
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <ScheduleIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      送信日時: {formatDate(messageDetail.message.sent_at)}
                    </Typography>
                  </Box>

                  {messageDetail.message.read_at && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <VisibilityIcon sx={{ mr: 1, color: 'warning.main' }} />
                      <Typography variant="body2" color="warning.main">
                        開封日時: {formatDate(messageDetail.message.read_at)}
                      </Typography>
                    </Box>
                  )}

                  {messageDetail.message.replied_at && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <ReplyIcon sx={{ mr: 1, color: 'success.main' }} />
                      <Typography variant="body2" color="success.main">
                        返信日時: {formatDate(messageDetail.message.replied_at)}
                      </Typography>
                    </Box>
                  )}

                  <Typography variant="body2" color="text.secondary">
                    送信者: {messageDetail.message.sender.name}
                    {messageDetail.message.sender.title &&
                      ` (${messageDetail.message.sender.title})`}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>

            {/* メッセージ本文 */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 'bold', mb: 2 }}
              >
                メッセージ内容
              </Typography>
              <Box
                sx={{
                  p: 2,
                  bgcolor: 'grey.50',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'grey.200',
                }}
              >
                <LinkifiedText
                  text={messageDetail.message.message_body}
                  variant="body1"
                  sx={{ lineHeight: 1.6 }}
                />
              </Box>
            </Paper>

            {/* 返信履歴 */}
            {messageDetail.replies && messageDetail.replies.length > 0 && (
              <Paper sx={{ p: 3 }}>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 'bold', mb: 2 }}
                >
                  返信履歴 ({messageDetail.replies.length}件)
                </Typography>

                {messageDetail.replies.map(
                  (reply: MessageReplyItem, index: number) => (
                    <Box
                      key={reply.id}
                      sx={{
                        mb: index < messageDetail.replies.length - 1 ? 3 : 0,
                      }}
                    >
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', mb: 1 }}
                      >
                        <Avatar
                          sx={{
                            mr: 2,
                            bgcolor: 'success.main',
                            width: 32,
                            height: 32,
                          }}
                        >
                          <PersonIcon fontSize="small" />
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 'medium' }}
                          >
                            {reply.sender_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(reply.sent_at)}
                            {reply.read_at && ` • 開封済み`}
                          </Typography>
                        </Box>
                      </Box>

                      <Box sx={{ ml: 5 }}>
                        <Box
                          sx={{
                            p: 2,
                            bgcolor: 'success.50',
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'success.200',
                          }}
                        >
                          <LinkifiedText
                            text={reply.message_body}
                            variant="body2"
                          />
                        </Box>

                        {reply.additional_question && (
                          <Box
                            sx={{
                              p: 2,
                              mt: 1,
                              bgcolor: 'info.50',
                              borderRadius: 1,
                              border: '1px solid',
                              borderColor: 'info.200',
                            }}
                          >
                            <Typography
                              variant="body2"
                              component="strong"
                              sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}
                            >
                              追加質問:
                            </Typography>
                            <LinkifiedText
                              text={reply.additional_question}
                              variant="body2"
                            />
                          </Box>
                        )}
                      </Box>

                      {index < messageDetail.replies.length - 1 && (
                        <Divider sx={{ mt: 2 }} />
                      )}
                    </Box>
                  )
                )}
              </Paper>
            )}

            {/* 統計情報 */}
            <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <Typography variant="body2" color="text.secondary">
                    開封状況
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                    {messageDetail.stats.is_read ? '開封済み' : '未開封'}
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="body2" color="text.secondary">
                    返信状況
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                    {messageDetail.stats.is_replied ? '返信あり' : '未返信'}
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="body2" color="text.secondary">
                    返信数
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                    {messageDetail.stats.total_replies}件
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button onClick={handleClose} variant="outlined">
          閉じる
        </Button>
      </DialogActions>
    </Dialog>
  );
};
